import { useState } from "react";

import MergeIcon from "@mui/icons-material/MergeRounded";
import { useConfirmationDialogContext } from "@shared";

import { ProceedingParticipantSearchResult } from "@/api";
import { useTranslation } from "@/translations";

import { useMergeParticipants } from "./useMergeParticipants";

export function useMergeParticipantsDialog(
  selectedParticipants: ProceedingParticipantSearchResult[],
  onMerged: () => void,
  onClose: () => void
) {
  const { t } = useTranslation();
  const { showConfirmationDialog } = useConfirmationDialogContext();
  const [destinationId, setDestinationId] = useState<string>(
    () => selectedParticipants[0]?.proceedingParticipantId ?? ""
  );

  const mergeMutation = useMergeParticipants(() => {
    onMerged();
    onClose();
  });

  const destination =
    selectedParticipants.find((p) => p.proceedingParticipantId === destinationId) ?? selectedParticipants[0];
  const source = selectedParticipants.find((p) => p.proceedingParticipantId !== destination?.proceedingParticipantId);

  const confirmMerge = () => {
    if (!destination || !source) {
      return;
    }
    showConfirmationDialog({
      dialogTitleText: t("proceedingParticipants.merge.confirmDialogTitle"),
      dialogText: t("proceedingParticipants.merge.confirmBody", {
        source: source.name,
        destination: destination.name,
      }),
      agreeText: t("proceedingParticipants.merge.confirmButton"),
      agreeIcon: <MergeIcon />,
      discardText: t("proceedingParticipants.merge.cancelButton"),
      onClose: (confirmed) => {
        if (confirmed) {
          mergeMutation.mutate({
            destinationId: destination.proceedingParticipantId,
            sourceId: source.proceedingParticipantId,
          });
        }
      },
    });
  };

  return {
    destination,
    source,
    setDestinationId,
    confirmMerge,
    isPending: mergeMutation.isPending,
  };
}
